import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { trackPhoneClick, trackSocialClick } from '../../hooks/useAnalytics';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  const toggleDropdown = (menu: string) => {
    setOpenDropdown(openDropdown === menu ? null : menu);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-slate-900/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Mobile Menu Button - sol taraf (boşluk için) */}
          <div className="lg:hidden w-10" />

          {/* Logo */}
          <Link to="/" className="flex-shrink-0 lg:flex-none">
            <img
              src="https://static.readdy.ai/image/afa77d5851d304160205a8c0c44e2b32/182c3b73f0182d5caae7ffb991a4b872.png"
              alt="Fiber ve Güvenlik Çözümleri"
              className="h-16 w-auto"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-6">
            <Link
              to="/"
              className="text-white hover:text-cyan-400 transition-colors font-medium whitespace-nowrap"
            >
              Ana Sayfa
            </Link>

            {/* Fiber Hizmetleri Dropdown */}
            <div className="relative group">
              <button
                className="text-white hover:text-cyan-400 transition-colors font-medium flex items-center gap-1 whitespace-nowrap"
                onMouseEnter={() => setOpenDropdown('fiber')}
              >
                Fiber Optik Hizmetleri
                <i className="ri-arrow-down-s-line"></i>
              </button>
              <div
                className="absolute top-full left-0 mt-2 w-64 bg-slate-800 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 py-2"
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <Link to="/fiber-hizmetleri" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Tüm Fiber Hizmetleri</Link>
                <Link to="/fiber-hizmetleri/fiber-kablo-tamiri" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Fiber Kablo Tamiri</Link>
                <Link to="/fiber-hizmetleri/fiber-sonlandirma" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Fiber Sonlandırma ve Ek</Link>
                <Link to="/fiber-hizmetleri/otdr-test" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">OTDR Test ve Arıza Tespiti</Link>
                <Link to="/fiber-hizmetleri/bina-fiber-altyapi" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Bina Fiber Altyapı</Link>
                <Link to="/fiber-hizmetleri/fabrika-fiber" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Fabrika Fiber Çözümleri</Link>
                <Link to="/fiber-hizmetleri/acil-servis" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">7/24 Acil Fiber Servisi</Link>
              </div>
            </div>

            {/* Güvenlik Sistemleri Dropdown */}
            <div className="relative group">
              <button
                className="text-white hover:text-cyan-400 transition-colors font-medium flex items-center gap-1 whitespace-nowrap"
                onMouseEnter={() => setOpenDropdown('security')}
              >
                Güvenlik Sistemleri
                <i className="ri-arrow-down-s-line"></i>
              </button>
              <div
                className="absolute top-full left-0 mt-2 w-64 bg-slate-800 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 py-2"
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <a href="/guvenlik-sistemleri" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Tüm Güvenlik Sistemleri</a>
                <a href="/guvenlik-sistemleri/ip-kamera-sistemleri" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">IP Kamera Sistemleri</a>
                <a href="/guvenlik-sistemleri/hirsiz-alarm-sistemleri" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Hırsız Alarm Sistemleri</a>
                <a href="/guvenlik-sistemleri/yangin-alarm-sistemleri" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Yangın Alarm Sistemleri</a>
                <a href="/guvenlik-sistemleri/gecis-kontrol-sistemleri" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Geçiş Kontrol Sistemleri</a>
                <a href="/guvenlik-sistemleri/bariyer-sistemleri" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Bariyer Sistemleri</a>
                <a href="/guvenlik-sistemleri/turnike-sistemleri" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Turnike Sistemleri</a>
              </div>
            </div>

            {/* Hizmet Bölgeleri Dropdown */}
            <div className="relative group">
              <button
                className="text-white hover:text-cyan-400 transition-colors font-medium flex items-center gap-1 whitespace-nowrap"
                onMouseEnter={() => setOpenDropdown('locations')}
              >
                Hizmet Bölgeleri
                <i className="ri-arrow-down-s-line"></i>
              </button>
              <div
                className="absolute top-full left-0 mt-2 w-56 bg-slate-800 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 py-2"
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <Link to="/bolgeler/istanbul-avrupa" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">İstanbul Avrupa</Link>
                <Link to="/bolgeler/istanbul-anadolu" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">İstanbul Anadolu</Link>
                <Link to="/bolgeler/kocaeli" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Kocaeli</Link>
                <Link to="/bolgeler/gebze" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Gebze</Link>
                <Link to="/bolgeler/tuzla" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Tuzla</Link>
                <Link to="/bolgeler/dilovasi" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Dilovası</Link>
                <Link to="/bolgeler/cayirova" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Çayırova</Link>
                <Link to="/bolgeler/bursa" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Bursa</Link>
                <Link to="/bolgeler/sakarya" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Sakarya</Link>
                <Link to="/bolgeler/yalova" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Yalova</Link>
                <Link to="/bolgeler/tekirdag" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Tekirdağ</Link>
                <Link to="/bolgeler/corlu" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Çorlu</Link>
                <Link to="/bolgeler/atasehir" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Ataşehir</Link>
                <Link to="/bolgeler/kadikoy" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Kadıköy</Link>
                <Link to="/bolgeler/umraniye" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Ümraniye</Link>
                <Link to="/bolgeler/esenyurt" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Esenyurt</Link>
                <Link to="/bolgeler/basaksehir" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Başakşehir</Link>
                <Link to="/bolgeler/pendik" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Pendik</Link>
                <Link to="/bolgeler/kartal" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Kartal</Link>
                <Link to="/bolgeler/beylikduzu" className="block px-4 py-2 text-gray-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors whitespace-nowrap">Beylikdüzü</Link>
              </div>
            </div>

            <Link
              to="/blog"
              className="text-white hover:text-cyan-400 transition-colors font-medium whitespace-nowrap"
            >
              Blog
            </Link>

            <Link
              to="/iletisim"
              className="text-white hover:text-cyan-400 transition-colors font-medium whitespace-nowrap"
            >
              İletişim
            </Link>

            {/* Desktop Sosyal Medya İkonları */}
            <div className="flex items-center gap-2 ml-4 border-l border-slate-700 pl-4">
              <a
                href="https://www.instagram.com/fiber_guvenlik/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-gradient-to-br hover:from-pink-500 hover:to-orange-400 hover:shadow-xl hover:shadow-pink-500/50 rounded-full transition-all duration-300 hover:scale-125 cursor-pointer text-white"
                aria-label="Instagram"
              >
                <i className="ri-instagram-line text-lg"></i>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61587743353286"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-sky-600 hover:shadow-xl hover:shadow-sky-500/50 rounded-full transition-all duration-300 hover:scale-125 cursor-pointer text-white"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-lg"></i>
              </a>
              <a
                href="https://www.youtube.com/channel/UCre9bsVpBqkHVtniQuVfYdA"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-red-600 hover:shadow-xl hover:shadow-red-500/50 rounded-full transition-all duration-300 hover:scale-125 cursor-pointer text-white"
                aria-label="YouTube"
              >
                <i className="ri-youtube-fill text-lg"></i>
              </a>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2 w-10 flex items-center justify-center"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-slate-900/98 backdrop-blur-lg border-t border-slate-800">
          <div className="px-4 py-4 space-y-3 text-center">
            <Link to="/" className="block text-white hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Ana Sayfa</Link>

            {/* Mobile Fiber Dropdown */}
            <div>
              <button
                onClick={() => toggleDropdown('fiber')}
                className="w-full flex items-center justify-center text-white hover:text-cyan-400 transition-colors py-2 whitespace-nowrap"
              >
                Fiber Optik Hizmetleri
                <i className={`ri-arrow-${openDropdown === 'fiber' ? 'up' : 'down'}-s-line ml-2`}></i>
              </button>
              {openDropdown === 'fiber' && (
                <div className="space-y-2 mt-2">
                  <Link to="/fiber-hizmetleri" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Tüm Fiber Hizmetleri</Link>
                  <Link to="/fiber-hizmetleri/fiber-kablo-tamiri" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Fiber Kablo Tamiri</Link>
                  <Link to="/fiber-hizmetleri/fiber-sonlandirma" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Fiber Sonlandırma ve Ek</Link>
                  <Link to="/fiber-hizmetleri/otdr-test" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">OTDR Test ve Arıza Tespiti</Link>
                  <Link to="/fiber-hizmetleri/bina-fiber-altyapi" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Bina Fiber Altyapı</Link>
                  <Link to="/fiber-hizmetleri/fabrika-fiber" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Fabrika Fiber Çözümleri</Link>
                  <Link to="/fiber-hizmetleri/acil-servis" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">7/24 Acil Fiber Servisi</Link>
                </div>
              )}
            </div>

            {/* Mobile Security Dropdown */}
            <div>
              <button
                onClick={() => toggleDropdown('security')}
                className="w-full flex items-center justify-center text-white hover:text-cyan-400 transition-colors py-2 whitespace-nowrap"
              >
                Güvenlik Sistemleri
                <i className={`ri-arrow-${openDropdown === 'security' ? 'up' : 'down'}-s-line ml-2`}></i>
              </button>
              {openDropdown === 'security' && (
                <div className="space-y-2 mt-2">
                  <a href="/guvenlik-sistemleri" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Tüm Güvenlik Sistemleri</a>
                  <a href="/guvenlik-sistemleri/ip-kamera-sistemleri" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">IP Kamera Sistemleri</a>
                  <a href="/guvenlik-sistemleri/hirsiz-alarm-sistemleri" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Hırsız Alarm Sistemleri</a>
                  <a href="/guvenlik-sistemleri/yangin-alarm-sistemleri" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Yangın Alarm Sistemleri</a>
                  <a href="/guvenlik-sistemleri/gecis-kontrol-sistemleri" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Geçiş Kontrol Sistemleri</a>
                  <a href="/guvenlik-sistemleri/bariyer-sistemleri" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Bariyer Sistemleri</a>
                  <a href="/guvenlik-sistemleri/turnike-sistemleri" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Turnike Sistemleri</a>
                </div>
              )}
            </div>

            {/* Mobile Locations Dropdown */}
            <div>
              <button
                onClick={() => toggleDropdown('locations')}
                className="w-full flex items-center justify-center text-white hover:text-cyan-400 transition-colors py-2 whitespace-nowrap"
              >
                Hizmet Bölgeleri
                <i className={`ri-arrow-${openDropdown === 'locations' ? 'up' : 'down'}-s-line ml-2`}></i>
              </button>
              {openDropdown === 'locations' && (
                <div className="space-y-2 mt-2">
                  <Link to="/bolgeler/istanbul-avrupa" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">İstanbul Avrupa</Link>
                  <Link to="/bolgeler/istanbul-anadolu" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">İstanbul Anadolu</Link>
                  <Link to="/bolgeler/kocaeli" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Kocaeli</Link>
                  <Link to="/bolgeler/gebze" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Gebze</Link>
                  <Link to="/bolgeler/tuzla" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Tuzla</Link>
                  <Link to="/bolgeler/dilovasi" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Dilovası</Link>
                  <Link to="/bolgeler/cayirova" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Çayırova</Link>
                  <Link to="/bolgeler/bursa" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Bursa</Link>
                  <Link to="/bolgeler/sakarya" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Sakarya</Link>
                  <Link to="/bolgeler/yalova" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Yalova</Link>
                  <Link to="/bolgeler/tekirdag" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Tekirdağ</Link>
                  <Link to="/bolgeler/corlu" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Çorlu</Link>
                  <Link to="/bolgeler/atasehir" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Ataşehir</Link>
                  <Link to="/bolgeler/kadikoy" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Kadıköy</Link>
                  <Link to="/bolgeler/umraniye" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Ümraniye</Link>
                  <Link to="/bolgeler/esenyurt" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Esenyurt</Link>
                  <Link to="/bolgeler/basaksehir" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Başakşehir</Link>
                  <Link to="/bolgeler/pendik" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Pendik</Link>
                  <Link to="/bolgeler/kartal" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Kartal</Link>
                  <Link to="/bolgeler/beylikduzu" className="block text-gray-300 hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Beylikdüzü</Link>
                </div>
              )}
            </div>

            <Link to="/blog" className="block text-white hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">Blog</Link>
            <Link to="/iletisim" className="block text-white hover:text-cyan-400 transition-colors py-2 whitespace-nowrap">İletişim</Link>

            {/* Mobile Sosyal Medya İkonları */}
            <div className="flex items-center justify-center gap-5 pt-4 mt-4 border-t border-slate-700">
              <a
                href="https://www.instagram.com/fiber_guvenlik/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-gradient-to-br hover:from-pink-500 hover:to-orange-400 hover:shadow-xl hover:shadow-pink-500/50 rounded-full transition-all duration-300 hover:scale-110 cursor-pointer text-white"
                aria-label="Instagram"
              >
                <i className="ri-instagram-line text-2xl"></i>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61587743353286"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-sky-600 hover:shadow-xl hover:shadow-sky-500/50 rounded-full transition-all duration-300 hover:scale-110 cursor-pointer text-white"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-2xl"></i>
              </a>
              <a
                href="https://www.youtube.com/channel/UCre9bsVpBqkHVtniQuVfYdA"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-red-600 hover:shadow-xl hover:shadow-red-500/50 rounded-full transition-all duration-300 hover:scale-110 cursor-pointer text-white"
                aria-label="YouTube"
              >
                <i className="ri-youtube-fill text-2xl"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
